import { useState } from 'react';
import PropTypes from 'prop-types';
// Utils
import { cn } from '@repo/zolintra-utils';
// Components
import { Button } from '@repo/zolintra-ui';
// Hooks
import { useTranslation } from 'react-i18next';
import useSearchParams from '../../../hooks/useSearchParams';
import { useAppContext } from '../../../store/AppContext';
// Permissions
import hasPermission, { PERMISSIONS } from '../../../permissions/hasPermission';
// Constants
import { VIEW_DOMAIN } from '../../../constants';
// Styles
import styles from './NewPostDialog.module.css';

function NewPostDialog({ forms }) {
  // Hooks
  const { t } = useTranslation();
  const [{ user }] = useAppContext();
  const { getSearchParams } = useSearchParams();

  // Permissions
  const hasCreateRemoteWorkingPostPermission = (
    hasPermission(PERMISSIONS.CREATE_ANY_NEW_REMOTE_WORKING_POST, user.prm)
    || hasPermission(PERMISSIONS.CREATE_OWN_NEW_REMOTE_WORKING_POST, user.prm)
  );
  const hasCreateLeavePostPermission = (
    hasPermission(PERMISSIONS.CREATE_ANY_NEW_LEAVE_POST, user.prm)
    || hasPermission(PERMISSIONS.CREATE_OWN_NEW_LEAVE_POST, user.prm)
  );

  // State
  const getDefaultSelectedValue = () => {
    // user has permission for both leave and remote working post creation
    if (hasCreateRemoteWorkingPostPermission && hasCreateLeavePostPermission) {
      return getSearchParams().view || VIEW_DOMAIN.REMOTE_WORKING;
    }

    // user has only permission for remote working post creation
    if (hasCreateRemoteWorkingPostPermission && !hasCreateLeavePostPermission) {
      return VIEW_DOMAIN.REMOTE_WORKING;
    }

    // user has only permission for leave post creation
    if (hasCreateLeavePostPermission && !hasCreateRemoteWorkingPostPermission) {
      return VIEW_DOMAIN.LEAVE;
    }

    return null;
  };

  const [selectedDomain, setSelectedDomain] = useState(getDefaultSelectedValue());

  return (
    <>
      <p className={styles.label}>
        {t('remoteWorkingAndLeave.newPostDialog.postTypeLabel')}
      </p>

      <div className={cn([styles['group-buttons'], 'margin-bottom-m'])}>
        <Button
          onClick={() => setSelectedDomain(VIEW_DOMAIN.REMOTE_WORKING)}
          color={selectedDomain === VIEW_DOMAIN.REMOTE_WORKING ? 'primary200' : 'neutral500'}
          shape="rectangle"
          variant="bordered"
          disabled={!hasCreateRemoteWorkingPostPermission}
        >
          {t('remoteWorkingAndLeave.newPostDialog.remoteWorkingBtnLabel')}
        </Button>
        <Button
          onClick={() => setSelectedDomain(VIEW_DOMAIN.LEAVE)}
          color={selectedDomain === VIEW_DOMAIN.LEAVE ? 'primary200' : 'neutral500'}
          shape="rectangle"
          variant="bordered"
          disabled={!hasCreateLeavePostPermission}
        >
          {t('remoteWorkingAndLeave.newPostDialog.leaveBtnLabel')}
        </Button>
      </div>

      {forms[selectedDomain]}
    </>
  );
}

NewPostDialog.propTypes = {
  forms: PropTypes.shape({}),
};

NewPostDialog.defaultProps = {
  forms: {},
};

export default NewPostDialog;
